/**
 * Brokerage import matchers — resolve an extracted REGA contract's owner and
 * property to existing company records before the brokerage contract is created.
 *
 * Matching runs client-side over the owners/properties lists the wizard already
 * loads for its pickers — no extra lookup endpoint.
 *
 *   Owner:    extracted owner_id_number          ↔  owners.national_id
 *   Property: extracted property_registration_no AND/OR deed_number
 *             (extraction does NOT reliably distinguish RER vs deed)
 *             ↔  properties.deed_number OR properties.rer_number
 *
 * Identifiers are normalised before comparison — Eastern (Arabic-Indic / Persian)
 * digits are folded to ASCII and cosmetic separators (space, dash, slash, dot,
 * comma, underscore, backslash) stripped — so an OCR-extracted "١٠١٠/١٢٣٤٥", a
 * typed "1010-12345", and a stored "101012345" all compare equal. Without the
 * digit fold a gov-extracted (Arabic-digit) number silently fails to match the
 * manually-entered Western one → the e-broker flow re-creates a duplicate
 * property despite the dedup (ticket: e-broker وساطة duplicate property).
 */

/**
 * Canonicalise a deed / RER / national-id string for equality matching: fold
 * Arabic-Indic + extended (Persian) digits to ASCII 0-9, then strip cosmetic
 * separators. Letters are preserved (deed numbers can be alphanumeric, e.g.
 * "DEED-3" → "DEED3"), so only formatting — never identity — is removed.
 */
export function normaliseId(v: string | null | undefined): string {
  return (v ?? "")
    // Fold Eastern digits to ASCII by code-point offset — no lookup table, no
    // unreachable fallback branch. Arabic-Indic U+0660–0669 and extended
    // (Persian/Urdu) U+06F0–06F9 are what gov extraction (OCR) and Arabic
    // keyboards emit; folding lets "١٢٣" match a stored "123".
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[\s\-/.,_\\]/g, "");
}

export interface OwnerMatchCandidate {
  id: string;
  national_id: string | null;
}

/** First owner whose national_id equals the extracted owner id, else null. */
export function matchOwnerByNationalId<T extends OwnerMatchCandidate>(
  owners: T[],
  extractedNationalId: string | null | undefined,
): T | null {
  const target = normaliseId(extractedNationalId);
  if (!target) return null;
  return owners.find((o) => normaliseId(o.national_id) === target) ?? null;
}

export interface PropertyMatchCandidate {
  id: string;
  deed_number?: string | null;
  rer_number?: string | null;
}

/** The distinct, non-empty identifiers an extraction yields for a property. */
export function extractedPropertyIdentifiers(fields: {
  property_registration_no?: string | null;
  deed_number?: string | null;
}): string[] {
  const out = new Set<string>();
  for (const v of [fields.property_registration_no, fields.deed_number]) {
    const n = normaliseId(v);
    if (n) out.add(n);
  }
  return [...out];
}

/**
 * First property whose deed_number OR rer_number matches any of the extracted
 * identifiers, else null. (Extraction can't tell RER from deed, so we try both
 * extracted values against both stored columns.)
 */
export function matchPropertyByIdentifiers<T extends PropertyMatchCandidate>(
  properties: T[],
  identifiers: string[],
): T | null {
  const targets = new Set(identifiers.map(normaliseId).filter(Boolean));
  if (targets.size === 0) return null;
  return (
    properties.find((p) => {
      const deed = normaliseId(p.deed_number);
      const rer = normaliseId(p.rer_number);
      return (!!deed && targets.has(deed)) || (!!rer && targets.has(rer));
    }) ?? null
  );
}
